import { ITaskTemplateController } from "Interfaces/Controllers/ITaskTemplateController";
import { IEnvConfigUtils } from "Interfaces/Utils/IEnvConfigUtils";
import { inject, injectable } from "inversify";
import BadRequestException from "Models/Exceptions/BadRequestException";
import { TaskTemplate } from "Models/Tasks/TaskTemplate";
import { TaskTemplateStep } from "Models/Tasks/TaskTemplateStep";
import { TaskTemplateTag } from "Models/Tasks/TaskTemplateTag";
import Types from "ServiceProvider/Types";
import request from "../BaseRequest";
import { BaseTemplateController } from "./BaseTemplateController";

@injectable()
export class TaskTemplateController extends BaseTemplateController<TaskTemplate> implements ITaskTemplateController {
    constructor(@inject(Types.IEnvConfigUtils) envConfigUtils: IEnvConfigUtils) {
        super(envConfigUtils, "tasktemplates");
    }

    public async getArrangeTaskTemplatesAsync(): Promise<TaskTemplate[]> {
        this.init();

        const response = await request
            .get(`${this.endpoint}/arrange`)
            .set(this.headers);

        return response.body as TaskTemplate[];
    }

    public async getTaskTemplateTagsForCompanyAsync(): Promise<TaskTemplateTag[]> {
        this.init();

        const response = await request
            .get(`${this.endpoint}/tags`)
            .set(this.headers);

        return response.body as TaskTemplateTag[];
    }

    public async deleteByIdAsync(taskTemplateId: number): Promise<void> {
        this.init();

        await request
            .delete(`${this.endpoint}/${taskTemplateId}`)
            .set(this.headers);
    }

    public async getByIdAsync(taskTemplateId: number): Promise<TaskTemplate> {
        this.init();

        const response = await request
            .get(`${this.endpoint}/${taskTemplateId}`)
            .set(this.headers);

        return response.body as TaskTemplate;
    }

    public async insertOneAsync(taskTemplate: TaskTemplate): Promise<TaskTemplate> {
        this.init();

        const result = await request
            .post(this.endpoint)
            .send(taskTemplate)
            .set(this.headers)
            .then((response: any) => {
                return response.body as TaskTemplate;
            })
            .catch((error: any) => {
                throw this.handleSaveError(error);
            });

        return result;
    }

    public async updateOneAsync(taskTemplate: TaskTemplate): Promise<TaskTemplate> {
        this.init();

        const result = await request
            .put(`${this.endpoint}/${taskTemplate.id}`)
            .send(taskTemplate)
            .set(this.headers)
            .then((response: any) => {
                return response.body as TaskTemplate;
            })
            .catch((error: any) => {
                throw this.handleSaveError(error);
            });

        return result;
    }

    public async updatePositionIndexAsync(taskTemplates: TaskTemplate[]): Promise<void> {
        this.init();

        const data = {};
        taskTemplates.forEach(x => {
            data[`${x.id}`] = x.positionIndex;
        });

        await request
            .put(`${this.endpoint}/positionindexes`)
            .send(data)
            .set(this.headers);
    }

    private handleSaveError(error: any): Error {
        if (error.response.badRequest) {
            return new BadRequestException(error.response.body.validationErrors);
        }
        else {
            return error;
        }
    }

    public async uploadMediaAsync(taskTemplate: TaskTemplate, video?: File, videoThumbnail?: File, picture?: File, description?: File): Promise<TaskTemplate> {
        this.init();

        const formData = new FormData();
        video && formData.append("video", video, video.name);
        videoThumbnail && formData.append("videoThumbnail", videoThumbnail, videoThumbnail.name);
        picture && formData.append("picture", picture, picture.name);
        description && formData.append("description", description, description.name);

        const response = await request
            .post(`${this.endpoint}/${taskTemplate.id}/media`)
            .send(formData)
            .set(this.headers);

        return response.body as TaskTemplate;
    }

    public async uploadStepMediaAsync(taskTemplate: TaskTemplate, step: TaskTemplateStep): Promise<TaskTemplateStep> {
        this.init();

        const formData = new FormData();
        step.videoFile && formData.append("video", step.videoFile);
        step.videoThumbnailFile && formData.append("videoThumbnail", step.videoThumbnailFile);
        step.pictureFile && formData.append("picture", step.pictureFile);

        const response = await request
            .post(`${this.endpoint}/${taskTemplate.id}/steps/${step.id}/media`)
            .send(formData)
            .set(this.headers);

        return response.body as TaskTemplateStep;
    }
}


// WEBPACK FOOTER //
// ./src/Apis/Controllers/TaskTemplateController.ts